import React, { useMemo } from "react";
import moment from "moment";
import { Link, useSearchParams } from "react-router-dom";
import ListPage from "../../../../components/ListPage";
import ChipSingleSelect from "../../../../components/filters/ChipSingleSelect";
import StatusBadge from "../../../../components/StatusBadge";
import EmptyValue from "../../../../components/EmptyValue";
import usePurchaseOrders from "../../../hooks/usePurchaseOrders";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/pagination";

// Purchase orders — GET /api/inventory/orders. Moved from
// src/pages/inventory-procurement/purchase-orders/index.jsx into the pharmacy v2
// console, rebuilt on ListPage/DataTable/StatusBadge/ChipSingleSelect instead of the
// page-local Table/StatusBadge/FilterChip/CtaButton set (now deleted).
//
// `inventoryOrders.status` is its own enum (ORDERED/PARTIALLY_RECEIVED/RECEIVED/
// CANCELLED) — a DIFFERENT status machine from the userOrders one that
// src/v2/constants/order-status.js's STATUS_TONE_CLASSES registry exists for, so tones
// here are a small local map, not a lookup into that registry. SUGGESTED was removed
// from the backend enum entirely (classes/InventoryOrder/Constants.js) and dropped here
// too.
const STATUS_TONE = {
    ORDERED: "info",
    PARTIALLY_RECEIVED: "warning",
    RECEIVED: "success",
    CANCELLED: "error",
};

const STATUS_CHIPS = [
    { id: "", label: "All" },
    { id: "ORDERED", label: "Ordered" },
    { id: "PARTIALLY_RECEIVED", label: "Partially received" },
    { id: "RECEIVED", label: "Received" },
    { id: "CANCELLED", label: "Cancelled" },
];

const PARAM = { status: "status", page: "page" };

function PurchaseOrdersPage() {
    const [searchParams, setSearchParams] = useSearchParams();

    const status = searchParams.get(PARAM.status) || "";
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    const query = usePurchaseOrders({ page, size: DEFAULT_PAGE_SIZE, status });
    const pagination = query.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };
    const rows = query.data?.rows ?? [];

    const updateParams = (changes, { resetPage = true } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
        });
        if (resetPage) next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    const columns = useMemo(
        () => [
            {
                key: "id",
                label: "PO id",
                render: (row) => (
                    <Link to={`/v2/inventory/procurement/purchase-orders/${row.id}`} className="font-semibold text-[var(--ops-emerald-600)] hover:underline">
                        #{row.id}
                    </Link>
                ),
            },
            { key: "vendor", label: "Vendor", render: (row) => row.vendor?.name || <EmptyValue /> },
            {
                key: "placedAt",
                label: "Placed at",
                render: (row) => (row.placedAt ? moment(row.placedAt).format("DD MMM YYYY, h:mm A") : <EmptyValue />),
            },
            {
                key: "placedBy",
                label: "Placed by",
                // `placedByName` is resolved server-side from the MyTatva userId in
                // `placedBy` (classes/InventoryOrder/Functions.js, one batched call per
                // page). Falls back to the raw id: an unresolvable user is still better
                // identified by its id than by a dash.
                render: (row) => (
                    <span className={row.placedByName ? "" : "text-xs text-[var(--ops-carbon-500)]"}>
                        {row.placedByName || row.placedBy || <EmptyValue />}
                    </span>
                ),
            },
            { key: "lineCount", label: "Line count", render: (row) => row.inventoryOrderItems?.length || 0 },
            {
                key: "status",
                label: "Status",
                render: (row) => <StatusBadge label={row.status} tone={STATUS_TONE[row.status] || "neutral"} />,
            },
        ],
        []
    );

    const filters = (
        <section aria-label="Filters" className="rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-4">
            <ChipSingleSelect
                label="Status"
                inline
                options={STATUS_CHIPS.map((chip) => ({ id: chip.id, label: chip.label }))}
                value={status}
                onChange={(nextStatus) => updateParams({ [PARAM.status]: nextStatus })}
            />
        </section>
    );

    return (
        <ListPage
            testId="pharmacy-procurement-purchase-orders"
            title="Purchase orders"
            subtitle="Orders placed against inventory shortages."
            actions={
                <Link to="/v2/inventory/procurement" className="text-sm font-semibold text-[var(--ops-emerald-600)] hover:underline">
                    ← Back to shortages
                </Link>
            }
            filters={filters}
            columns={columns}
            rows={rows}
            query={query}
            pagination={pagination}
            onPageChange={(nextPage) => updateParams({ [PARAM.page]: String(nextPage) }, { resetPage: false })}
            emptyTitle="No purchase orders"
            emptyHint="No purchase orders in this view."
        />
    );
}

export default PurchaseOrdersPage;
